import { inject, Injectable } from '@angular/core';
import { StateContext } from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';
import { TableStateModel } from '../table.state.model';
import { Pagination } from '@core/models/pagination.model';
import { TableActions } from '../table.actions';
import { PartnerService } from '@core/services/partner.service';
import { NotificationFacade } from '@core/facades/notification.facade';

@Injectable({
  providedIn: 'root',
})
export class PartnersHandler {
  partnerService = inject(PartnerService);
  notify = inject(NotificationFacade);

  handlePartnersLoad(ctx: StateContext<TableStateModel>, params: Pagination) {
    this.partnerService.getPartners({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'partners',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'partners',
            error: error.message,
          })
        );
      },
    });
  }

  filterPartnersList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterPartnersList
  ) {
    return this.partnerService
      .getPartners(undefined, action.payload.filter)
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'partners',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'partners',
              error: error.message,
            })
          );
        },
      });
  }

  handlePartnerKycDocumentsLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.partnerService.getPartnerKycDocuments({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'partnerKycDocuments',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'partnerKycDocuments',
            error: error.message,
          })
        );
      },
    });
  }

  filterPartnerKycDocumentList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterPartnerKycDocumentList
  ) {
    return this.partnerService
      .getPartnerKycDocuments(undefined, action.payload.filter)
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'partnerKycDocuments',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'partnerKycDocuments',
              error: error.message,
            })
          );
        },
      });
  }

  updatePartnerKycDocumentStatus(
    ctx: StateContext<TableStateModel>,
    action: TableActions.UpdatePartnerKycDocumentStatus
  ) {
    const { documentId, status, rejectionReason } = action.payload;

    return this.partnerService
      .updatePartnerKycDocumentStatus({
        document_id: documentId,
        status,
        ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
      })
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const documents = state.partnerKycDocuments?.data.map((document) =>
            document.id === documentId
              ? {
                  ...document,
                  status,
                  rejection_reason:
                    rejectionReason ?? document.rejection_reason,
                }
              : document
          );

          ctx.patchState({
            partnerKycDocuments: {
              ...state.partnerKycDocuments,
              data: documents,
            },
          } as Partial<TableStateModel>);

          this.notify.success(
            status === 'approved'
              ? 'Document approved successfully'
              : 'Document rejected successfully'
          );
        }),
        catchError((error) => {
          this.notify.error(
            status === 'approved'
              ? 'Failed to approve document'
              : 'Failed to reject document'
          );
          return throwError(() => error);
        })
      );
  }

  deletePartner(
    ctx: StateContext<TableStateModel>,
    action: TableActions.DeletePartner
  ) {
    return this.partnerService
      .deletePartner(action.payload.partnerId)
      .subscribe({
        next: () => {
          const state = ctx.getState();
          const partners = state.partners?.data.filter(
            (partner) => partner.id !== action.payload.partnerId
          );

          ctx.patchState({
            partners: {
              ...state.partners,
              data: partners,
              totalCount: Math.max((state.partners?.totalCount ?? 1) - 1, 0),
            },
          } as Partial<TableStateModel>);

          this.notify.success('Partner deleted successfully');
        },
        error: () => {
          this.notify.error('Failed to delete partner');
        },
      });
  }
}
